import { useEffect, useRef, useState } from "react"

import { Card } from "@/components/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from "@/components/table"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { DesignComponentsSubItem } from "@/domains/navigation/subitems/design-components-subitem"
import { DesignSubItem } from "@/domains/navigation/subitems/design-subitem"

export const DESIGN_BREADCRUMB_ITEMS = [new DesignSubItem(), new DesignComponentsSubItem()]

type ComponentVariable = {
  name: string
  description: string
  cssVar: string
}

const TABLE_VARIABLES: ComponentVariable[] = [
  { name: "Background", description: "Table container background", cssVar: "table-bg" },
  { name: "Border", description: "Table container border", cssVar: "table-border" },
  { name: "Header Background", description: "Table header background", cssVar: "table-head-bg" },
  { name: "Divider", description: "Row divider color", cssVar: "table-divider" },
  { name: "Row Background", description: "Table row background", cssVar: "table-row-bg" },
  {
    name: "Row Stripe Background",
    description: "Alternating row background (zebra striping)",
    cssVar: "table-row-alt-bg",
  },
  { name: "Row Border", description: "Table row top border", cssVar: "table-row-border" },
  { name: "Cell Background", description: "Table cell background", cssVar: "table-cell-bg" },
  { name: "Cell Hover Background", description: "Table cell hover background", cssVar: "table-cell-hover-bg" },
]

const P_VARIABLES = [
  "p100-light",
  "p100",
  "p100-shade",
  "p200-light",
  "p200",
  "p200-shade",
  "p300-light",
  "p300",
  "p300-shade",
  "p400-light",
  "p400",
  "p400-shade",
  "p500",
  "transparent",
] as const

type PVariable = (typeof P_VARIABLES)[number]

const getDefaultMapping = (cssVar: string): PVariable => {
  const defaults: Record<string, PVariable> = {
    "table-bg": "p300",
    "table-border": "p300",
    "table-head-bg": "p300",
    "table-divider": "p400",
    "table-row-bg": "p400",
    "table-row-alt-bg": "p400",
    "table-row-border": "transparent",
    "table-cell-bg": "transparent",
    "table-cell-hover-bg": "p400-light",
  }
  return defaults[cssVar] ?? "p400"
}

const updateCSSVariable = (cssVar: string, pVar: PVariable) => {
  // Handle transparent for cell-bg and row-border
  if ((cssVar === "table-cell-bg" || cssVar === "table-row-border") && pVar === "transparent") {
    document.documentElement.style.setProperty(`--${cssVar}`, "transparent")
  } else {
    // Get the actual HSL values from the p variable and set directly
    const hslValues = getComputedStyle(document.documentElement).getPropertyValue(`--${pVar}`).trim()
    const value = `hsl(${hslValues})`
    document.documentElement.style.setProperty(`--${cssVar}`, value)
    // Force a reflow to ensure the change is applied
    void document.documentElement.offsetHeight
  }
}

const getColorValue = (pVar: PVariable): string => {
  // Handle transparent
  if (pVar === "transparent") {
    return "transparent"
  }
  // Get the HSL values from the CSS variable and wrap in hsl()
  const hslValues = getComputedStyle(document.documentElement).getPropertyValue(`--${pVar}`).trim()
  return `hsl(${hslValues})`
}

type ColorSwatchPickerProps = {
  value: PVariable
  onChange: (value: PVariable) => void
  label: string
}

const ColorSwatchPicker = ({ value, onChange, label }: ColorSwatchPickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const currentColor = getColorValue(value)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-3 rounded border border-p400-light bg-p300 px-3 py-2 text-left text-sm text-t100 transition-colors hover:bg-p300-light focus:border-p400-light focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <div
          className="h-6 w-6 flex-shrink-0 rounded border border-p400-light"
          style={{ backgroundColor: currentColor }}
        />
        <span className="flex-1 font-mono">{value}</span>
        <span className="text-t300">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded border border-p400-light bg-black p-2 shadow-lg">
          <div className="grid grid-cols-6 gap-0">
            {P_VARIABLES.map((pVar) => {
              const color = getColorValue(pVar)
              const isSelected = pVar === value
              const isTransparent = pVar === "transparent"
              return (
                <button
                  key={pVar}
                  type="button"
                  onClick={() => {
                    onChange(pVar)
                    setIsOpen(false)
                  }}
                  className="group relative aspect-square transition-opacity hover:opacity-80"
                  style={
                    isTransparent
                      ? {
                          backgroundImage:
                            "linear-gradient(45deg, #666 25%, transparent 25%), linear-gradient(-45deg, #666 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #666 75%), linear-gradient(-45deg, transparent 75%, #666 75%)",
                          backgroundSize: "8px 8px",
                          backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
                          backgroundColor: "#333",
                        }
                      : { backgroundColor: color }
                  }
                >
                  <span
                    className={`absolute inset-0 flex items-center justify-center font-mono text-xs ${
                      isSelected ? "font-semibold text-primary-300" : isTransparent ? "text-white/90" : "text-white/80"
                    }`}
                    style={{
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.8), 0 0 4px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {pVar}
                  </span>
                  {isSelected && (
                    <div className="absolute right-1 top-1 font-bold text-primary-300 drop-shadow-lg">✓</div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const DesignComponentsTablePage = () => {
  const [mappings, setMappings] = useState<Record<string, PVariable>>({})
  const [updateKey, setUpdateKey] = useState(0)

  useEffect(() => {
    // Load saved mappings from localStorage
    const savedMappings = localStorage.getItem("component-table-mappings")
    if (savedMappings) {
      try {
        const parsed = JSON.parse(savedMappings)
        setMappings(parsed)
        // Apply saved mappings
        Object.entries(parsed).forEach(([cssVar, pVar]) => {
          updateCSSVariable(cssVar, pVar as PVariable)
        })
      } catch (error) {
        console.error("Failed to load saved mappings:", error)
      }
    } else {
      // Initialize with defaults
      const defaultMappings: Record<string, PVariable> = {}
      TABLE_VARIABLES.forEach((variable) => {
        const defaultValue = getDefaultMapping(variable.cssVar)
        defaultMappings[variable.cssVar] = defaultValue
        updateCSSVariable(variable.cssVar, defaultValue)
      })
      setMappings(defaultMappings)
    }
  }, [])

  const handleMappingChange = (cssVar: string, pVar: PVariable) => {
    const newMappings = { ...mappings, [cssVar]: pVar }
    setMappings(newMappings)
    updateCSSVariable(cssVar, pVar)
    // Save to localStorage
    localStorage.setItem("component-table-mappings", JSON.stringify(newMappings))
    // Force a re-render to ensure styles are updated
    setUpdateKey((prev) => prev + 1)

    // Debug: Verify the variable was set
    if (process.env.NODE_ENV === "development") {
      const computed = getComputedStyle(document.documentElement).getPropertyValue(`--${cssVar}`).trim()
      console.log(`Updated --${cssVar} to:`, computed)
    }
  }

  const handleReset = () => {
    // Reset all mappings to defaults
    const defaultMappings: Record<string, PVariable> = {}
    TABLE_VARIABLES.forEach((variable) => {
      const defaultValue = getDefaultMapping(variable.cssVar)
      defaultMappings[variable.cssVar] = defaultValue
      updateCSSVariable(variable.cssVar, defaultValue)
    })
    setMappings(defaultMappings)
    // Clear localStorage to use defaults
    localStorage.removeItem("component-table-mappings")
    // Force a re-render
    setUpdateKey((prev) => prev + 1)
  }

  return (
    <PageContainer>
      <MetaData title="Design | Components | Table" />
      <PageTitle title="Table Component" subTitle="Configure table component colors" items={DESIGN_BREADCRUMB_ITEMS} />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Table Preview */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-t100">Preview</h2>

          {/* Table inside p400 card */}
          <div className="space-y-2" key={`table-card-${updateKey}`}>
            <h3 className="text-sm font-medium text-t200">Inside p400 Card</h3>
            <Card.Container>
              <Table key={`table-1-${updateKey}`}>
                <TableHead>
                  <TableHeaderRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Date</TableHeader>
                  </TableHeaderRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Item 1</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>$1,234.56</TableCell>
                    <TableCell>2024-01-15</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Item 2</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>$2,345.67</TableCell>
                    <TableCell>2024-01-16</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Item 3</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>$3,456.78</TableCell>
                    <TableCell>2024-01-17</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card.Container>
          </div>

          {/* Table directly on p500 background */}
          <div className="space-y-2" key={`table-direct-${updateKey}`}>
            <h3 className="text-sm font-medium text-t200">Directly on p500 Background</h3>
            <Table key={`table-2-${updateKey}`}>
              <TableHead>
                <TableHeaderRow>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Amount</TableHeader>
                  <TableHeader>Date</TableHeader>
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Item 1</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>$1,234.56</TableCell>
                  <TableCell>2024-01-15</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Item 2</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>$2,345.67</TableCell>
                  <TableCell>2024-01-16</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Item 3</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>$3,456.78</TableCell>
                  <TableCell>2024-01-17</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Variable Mapper */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-t100">CSS Variable Mapping</h2>
            <button
              onClick={handleReset}
              className="rounded border border-p400-light bg-p300 px-3 py-1.5 text-xs text-t100 transition-colors hover:bg-p300-light"
              title="Reset all variables to defaults"
            >
              🔄 Reset
            </button>
          </div>
          <div className="rounded-lg border border-p400-light bg-p400 p-4">
            <div className="space-y-4">
              {TABLE_VARIABLES.map((variable) => (
                <div key={variable.cssVar} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-t100">{variable.name}</div>
                      <div className="text-xs text-t200">{variable.description}</div>
                      <div className="mt-1 font-mono text-xs text-t300">--{variable.cssVar}</div>
                    </div>
                  </div>
                  <ColorSwatchPicker
                    value={mappings[variable.cssVar] ?? getDefaultMapping(variable.cssVar)}
                    onChange={(pVar) => handleMappingChange(variable.cssVar, pVar)}
                    label={variable.name}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  )
}

export default DesignComponentsTablePage
